import { Control, Controller, FieldErrors } from "react-hook-form";

import { t } from "i18next";

import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";

import { type FeedbackFormValues } from "@/src/components/organisms/FeedbackForm/feedbackFormValidator";
import {
  CONSUMER_SUBJECT_OPTIONS,
  DEALER_SUBJECT_OPTIONS,
  FEEDBACK_SUBJECT_EMPTY
} from "@/src/components/organisms/FeedbackForm/feedbackSubjectOptions";

import { MAX_FEEDBACK_MESSAGE_LENGTH } from "@/src/config/baanaConfig";

interface FeedbackFormFieldsProps {
  control: Control<FeedbackFormValues>;
  errors: FieldErrors<FeedbackFormValues>;
  isFormDisabled: boolean;
  userType: "consumer" | "dealer";
}

const FeedbackFormFields = ({
  control,
  errors,
  isFormDisabled,
  userType
}: FeedbackFormFieldsProps) => {
  return (
    <>
      <Controller
        control={control}
        name="email"
        render={({ field }) => (
          <TextField
            {...field}
            label={t("feedbackForm.fields.email")}
            type="email"
            required
            fullWidth
            disabled={isFormDisabled}
            error={!!errors.email}
            helperText={errors.email?.message as string}
          />
        )}
      />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}
        >
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <TextField
                {...field}
                label={t("feedbackForm.fields.name")}
                fullWidth
                disabled={isFormDisabled}
                error={!!errors.name}
                helperText={errors.name?.message as string}
              />
            )}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}
        >
          <Controller
            control={control}
            name="mobile"
            render={({ field }) => (
              <TextField
                {...field}
                label={t("feedbackForm.fields.mobile")}
                fullWidth
                disabled={isFormDisabled}
                error={!!errors.mobile}
                helperText={errors.mobile?.message as string}
              />
            )}
          />
        </Grid>
      </Grid>
      <Controller
        control={control}
        name="issueType"
        render={({ field }) => {
          const options =
            userType === "dealer"
              ? DEALER_SUBJECT_OPTIONS
              : CONSUMER_SUBJECT_OPTIONS;
          return (
            <FormControl
              fullWidth
              required
              disabled={isFormDisabled}
              error={!!errors.issueType}
            >
              <InputLabel>{t("feedbackForm.fields.subject")}</InputLabel>
              <Select
                {...field}
                value={field.value || FEEDBACK_SUBJECT_EMPTY}
                label={t("feedbackForm.fields.subject")}
                onChange={(e) =>
                  field.onChange(e.target.value || FEEDBACK_SUBJECT_EMPTY)
                }
              >
                {options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {t(opt.i18nKey)}
                  </MenuItem>
                ))}
              </Select>
              {errors.issueType?.message ? (
                <FormHelperText>
                  {errors.issueType.message as string}
                </FormHelperText>
              ) : null}
            </FormControl>
          );
        }}
      />
      <Controller
        control={control}
        name="message"
        render={({ field }) => (
          <TextField
            {...field}
            label={t("feedbackForm.fields.message")}
            required
            fullWidth
            multiline
            minRows={4}
            disabled={isFormDisabled}
            error={!!errors.message}
            helperText={
              errors.message?.message
                ? (errors.message.message as string)
                : `${field.value?.length ?? 0} / ${MAX_FEEDBACK_MESSAGE_LENGTH}`
            }
            slotProps={{
              htmlInput: { maxLength: MAX_FEEDBACK_MESSAGE_LENGTH }
            }}
          />
        )}
      />
    </>
  );
};

export default FeedbackFormFields;
